
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import * as OM from '@/Model';

@Options({})
export default class EditReflectiveBandsAccessoryModal extends Vue {

    @Prop() estimationIdentifier: string;
    @Prop() productionProcessIdentifier: string;
    @Prop() reflectiveBands: OM.ReflectiveBandsCalculation;
    @Prop() codes: OM.ReflectiveBandCodeConfiguration[];
    @Prop() saveCallback: (model: OM.EditReflectiveBandsAccessoryVM) => Promise<any>;

    currentStep: number = 0;
    stepLabels: string[] = ["Code", "Raw material", "Transport", "Manufacturing", "Summary"];
    selectedCode: string = "";
    lengthCm: number = null;
    arealDensityGramsPerSquareMeter: number = null;
    distanceKm: number = null;
    adhesiveMassKg: number = null;
    electricityKwhPerKg: number = null;
    serverErrors: OM.MaterialCompositionError[] = [];

    created() {
        if (this.reflectiveBands && this.reflectiveBands.code) {
            this.selectedCode = this.reflectiveBands.code;
            this.lengthCm = this.reflectiveBands.lengthCm;
            this.arealDensityGramsPerSquareMeter = this.reflectiveBands.arealDensityGramsPerSquareMeter;
            this.distanceKm = this.reflectiveBands.distanceKm;
            this.adhesiveMassKg = this.reflectiveBands.adhesiveMassKg;
            this.electricityKwhPerKg = this.reflectiveBands.electricityKwhPerKg;
        }
    }

    get groupedCodes(): { name: string, items: OM.ReflectiveBandCodeConfiguration[] }[] {
        let groups: { [key: string]: OM.ReflectiveBandCodeConfiguration[] } = {};
        (this.codes || []).forEach(item => {
            if (!groups[item.group])
                groups[item.group] = [];
            groups[item.group].push(item);
        });
        return Object.keys(groups)
            .sort((a, b) => a.trim().localeCompare(b.trim(), undefined, { sensitivity: 'base' }))
            .map(name => ({
                name,
                items: groups[name].sort((a, b) => a.code.localeCompare(b.code, undefined, { sensitivity: 'base', numeric: true }))
            }));
    }

    get selectedConfiguration(): OM.ReflectiveBandCodeConfiguration {
        return (this.codes || []).find(x => x.code == this.selectedCode);
    }

    applySelectedCode() {
        let selected = this.selectedConfiguration;
        if (!selected)
            return;

        this.lengthCm = selected.defaultLengthCm;
        this.arealDensityGramsPerSquareMeter = selected.defaultArealDensityGramsPerSquareMeter;
        this.distanceKm = selected.defaultDistanceKm;
        this.adhesiveMassKg = selected.defaultAdhesiveMassKg;
        this.electricityKwhPerKg = selected.defaultElectricityKwhPerKg;
        this.serverErrors = [];
    }

    get rawMaterialInputsAreValid(): boolean {
        return Number(this.lengthCm) > 0 && Number(this.arealDensityGramsPerSquareMeter) > 0;
    }

    get transportInputsAreValid(): boolean {
        return Number(this.distanceKm) > 0;
    }

    get manufacturingInputsAreValid(): boolean {
        return Number(this.adhesiveMassKg) >= 0 && Number(this.electricityKwhPerKg) >= 0;
    }

    get allInputsAreValid(): boolean {
        return !!this.selectedConfiguration &&
            this.rawMaterialInputsAreValid &&
            this.transportInputsAreValid &&
            this.manufacturingInputsAreValid;
    }

    get currentStepIsValid(): boolean {
        if (this.currentStep == 0)
            return !!this.selectedConfiguration;
        if (this.currentStep == 1)
            return this.rawMaterialInputsAreValid;
        if (this.currentStep == 2)
            return this.transportInputsAreValid;
        if (this.currentStep == 3)
            return this.manufacturingInputsAreValid;
        return this.allInputsAreValid;
    }

    get finalGramsCo2e(): number {
        let item = this.selectedConfiguration;
        if (!item || !this.allInputsAreValid)
            return 0;

        let areaSquareMeters = item.heightCm * Number(this.lengthCm) / 10000;
        let baseMassKg = areaSquareMeters * Number(this.arealDensityGramsPerSquareMeter) / 1000;
        let rawMaterial = baseMassKg * item.rawMaterialEmissionFactorKgCo2ePerKg;
        let transport = baseMassKg / 1000 * Number(this.distanceKm) * item.transportEmissionFactorKgCo2ePerTonneKm;
        let manufacturing =
            item.binderMassKg * item.binderEmissionFactorKgCo2ePerKg +
            item.glassBeadsMassKg * item.glassBeadsEmissionFactorKgCo2ePerKg +
            item.frChemicalMassKg * item.frChemicalEmissionFactorKgCo2ePerKg +
            Number(this.adhesiveMassKg) * item.adhesiveEmissionFactorKgCo2ePerKg +
            baseMassKg * Number(this.electricityKwhPerKg) * item.gridElectricityEmissionFactorKgCo2ePerKwh;

        return (rawMaterial + transport + manufacturing) * 1000;
    }

    get globalErrors(): string[] {
        return this.serverErrors.map(x => x.message);
    }

    save() {
        let model = new OM.EditReflectiveBandsAccessoryVM();
        model.estimationIdentifier = this.estimationIdentifier;
        model.productionProcessIdentifier = this.productionProcessIdentifier;
        model.code = this.selectedCode;
        model.lengthCm = Number(this.lengthCm);
        model.arealDensityGramsPerSquareMeter = Number(this.arealDensityGramsPerSquareMeter);
        model.distanceKm = Number(this.distanceKm);
        model.adhesiveMassKg = Number(this.adhesiveMassKg);
        model.electricityKwhPerKg = Number(this.electricityKwhPerKg);

        this.serverErrors = [];
        this.saveCallback(model).catch(err => {
            this.serverErrors = err && err.errors ? err.errors : [];
        });
    }
}
